import React, { useState, useEffect, useRef } from 'react';
import FormInput from '../common/FormInput';
import FormButton from '../common/FormButton';
import SocietyPickerModal from './SocietyPickerModal';
import CityPickerModal from './CityPickerModal';
import { societyService } from '../../../services/societyService';

const StepTwo = ({ formData, onBack, onComplete, loading = false }) => {
  const [stepData, setStepData] = useState({
    name:
      formData.name ||
      [formData.first_name, formData.last_name].filter(Boolean).join(' ').trim(),
    gender: formData.gender || '',
    age: formData.age || '',
    country: formData.country || 'Pakistan', // Default to Pakistan
    city: formData.city || '',
    society: formData.society || ''
  });
  const [errors, setErrors] = useState({});
  const [societies, setSocieties] = useState([]);
  const [loadingSocieties, setLoadingSocieties] = useState(true);
  const [societyPopupOpen, setSocietyPopupOpen] = useState(false);
  const [societyPickerMode, setSocietyPickerMode] = useState('pick');
  const [cityPopupOpen, setCityPopupOpen] = useState(false);
  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(true);
  const [listMaxHeightPx, setListMaxHeightPx] = useState(360);
  const formRef = useRef(null);

  // Load cities on component mount
  useEffect(() => {
    const loadCities = async () => {
      try {
        setLoadingCities(true);
        const response = await societyService.getCities({ forRegistration: true });
        const citiesList = response.cities || [];
        // Sort cities alphabetically
        const sortedCities = citiesList.sort((a, b) => a.localeCompare(b));
        setCities(sortedCities);
      } catch (error) {
        console.error('Error loading cities:', error);
        setCities([]);
      } finally {
        setLoadingCities(false);
      }
    };

    loadCities();
  }, []);

  useEffect(() => {
    const setBoundedHeight = () => {
      const h = formRef.current?.offsetHeight || 0;
      if (h > 0) {
        setListMaxHeightPx(Math.max(220, Math.floor(h * 0.9)));
      }
    };
    setBoundedHeight();
    window.addEventListener('resize', setBoundedHeight);
    return () => window.removeEventListener('resize', setBoundedHeight);
  }, []);

  // Load societies when city changes
  useEffect(() => {
    const loadSocieties = async () => {
      if (!stepData.city) {
        setSocieties([]);
        return;
      }

      try {
        setLoadingSocieties(true);
        const allSocieties = await societyService.getSocietiesForCityAll(stepData.city, '', {
          forRegistration: true,
        });
        const sortedSocieties = allSocieties.sort((a, b) =>
          a.name.localeCompare(b.name)
        );
        setSocieties(sortedSocieties);
        
        // Clear society selection if current society is not in the new list
        if (stepData.society && !sortedSocieties.find(s => s.id.toString() === stepData.society)) {
          setStepData(prev => ({ ...prev, society: '' }));
        }
      } catch (error) {
        console.error('Error loading societies:', error);
        setSocieties([]);
      } finally {
        setLoadingSocieties(false);
      }
    };

    loadSocieties();
  }, [stepData.city]);

  const selectedSocietyName = stepData.society && societies.find(s => s.id.toString() === stepData.society)?.name;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setStepData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const selectCity = (city) => {
    setStepData((prev) => ({
      ...prev,
      city,
      society: prev.city === city ? prev.society : '',
    }));
    if (errors.city) {
      setErrors((prev) => ({ ...prev, city: '' }));
    }
  };

  const validateStep = () => {
    const newErrors = {};

    if (!stepData.name || !stepData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (stepData.age !== '' && stepData.age != null) {
      const ageNum = Number(stepData.age);
      if (Number.isNaN(ageNum) || ageNum < 13 || ageNum > 120) {
        newErrors.age = 'Age must be between 13 and 120';
      }
    }

    // Country is not required - defaults to Pakistan
    // if (!stepData.country) {
    //   newErrors.country = 'Country is required';
    // }

    if (!stepData.city) {
      newErrors.city = 'City is required';
    }

    if (!stepData.society) {
      newErrors.society = 'Please select a society';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleComplete = () => {
    if (validateStep()) {
      onComplete(stepData);
    }
  };

  const genderOptions = [
    { value: '', label: 'Select gender' },
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' },
    { value: 'other', label: 'Other' },
    { value: 'prefer_not_to_say', label: 'Prefer not to say' }
  ];

  return (
    <div className="space-y-6" ref={formRef}>
      <div className="text-center mb-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-2">Step 2: Personal Information</h3>
        <p className="text-gray-600">Tell us a bit about yourself</p>
      </div>

      <FormInput
        label="Name"
        name="name"
        value={stepData.name}
        onChange={handleChange}
        placeholder="Enter your full name"
        required
        error={errors.name}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Gender <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <select
            name="gender"
            value={stepData.gender}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.gender ? 'border-red-300' : 'border-gray-300'
            }`}
          >
            {genderOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {errors.gender && (
            <p className="mt-1 text-sm text-red-600">{errors.gender}</p>
          )}
        </div>

        <FormInput
          label="Age (optional)"
          type="number"
          name="age"
          value={stepData.age}
          onChange={handleChange}
          placeholder="Enter your age"
          error={errors.age}
          min="13"
          max="120"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          City <span className="text-red-500">*</span>
        </label>
        <button
          type="button"
          onClick={() => {
            if (!loadingCities) setCityPopupOpen(true);
          }}
          className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
            errors.city ? 'border-red-300' : 'border-gray-300'
          }`}
          disabled={loadingCities}
        >
          {loadingCities ? 'Loading cities...' : stepData.city || 'Select your city'}
        </button>
        {errors.city && (
          <p className="mt-1 text-sm text-red-600">{errors.city}</p>
        )}
        {!loadingCities && cities.length === 0 && (
          <p className="mt-1 text-sm text-gray-500">No cities available. Please contact support.</p>
        )}
        <CityPickerModal
          open={cityPopupOpen}
          onClose={() => setCityPopupOpen(false)}
          cities={cities}
          loading={loadingCities}
          selectedCity={stepData.city}
          onSelect={selectCity}
          maxPanelHeightPx={listMaxHeightPx}
        />
      </div>

      <div>
        <label htmlFor="society" className="block text-sm font-medium text-gray-700 mb-2">
          Society <span className="text-red-500">*</span>
        </label>
        <div className="flex items-center gap-2">
          <button
            type="button"
            id="society"
            onClick={() => {
              if (stepData.city && !loadingSocieties) {
                setSocietyPickerMode('pick');
                setSocietyPopupOpen(true);
              }
            }}
            disabled={!stepData.city || loadingSocieties}
            className={`flex-1 px-3 py-2 border rounded-md text-left shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed ${
              errors.society ? 'border-red-300' : 'border-gray-300'
            }`}
          >
            {!stepData.city
              ? 'Please select a city first'
              : loadingSocieties
                ? 'Loading societies...'
                : selectedSocietyName || (societies.length === 0 ? 'No societies found in this city' : 'Select your society')}
          </button>
          {!!stepData.city && (
            <button
              type="button"
              onClick={() => {
                setSocietyPickerMode('add');
                setSocietyPopupOpen(true);
              }}
              className="shrink-0 px-3 py-2 rounded-md border border-blue-200 bg-blue-50 text-blue-700 text-sm font-medium hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Add Society
            </button>
          )}
        </div>
        {errors.society && (
          <p className="mt-1 text-sm text-red-600">{errors.society}</p>
        )}

        <SocietyPickerModal
          open={societyPopupOpen}
          onClose={() => {
            setSocietyPopupOpen(false);
            setSocietyPickerMode('pick');
          }}
          city={stepData.city}
          requesterEmail={formData.email || ''}
          maxPanelHeightPx={listMaxHeightPx}
          initialMode={societyPickerMode}
          onSelect={(societyId, options) => {
            setStepData((prev) => ({ ...prev, society: societyId }));
            if (options?.name) {
              const id = parseInt(societyId, 10);
              setSocieties((prev) => {
                if (prev.some((s) => String(s.id) === String(societyId))) return prev;
                return [...prev, { id, name: options.name }].sort((a, b) =>
                  (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }),
                );
              });
            }
            if (errors.society) {
              setErrors((prev) => ({ ...prev, society: '' }));
            }
          }}
        />
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">Location Services</h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>We'll automatically detect your precise location when you use location-based features. This helps you find nearby businesses, events, and community members.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex space-x-4">
        <button
          onClick={onBack}
          className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-md font-medium transition-colors"
        >
          ← Back
        </button>
        
        <FormButton
          onClick={handleComplete}
          loading={loading}
          disabled={loading || !stepData.name?.trim() || !stepData.city || !stepData.society}
          className="flex-1"
        >
          {loading ? 'Creating Account...' : 'Create Account'}
        </FormButton>
      </div>
    </div>
  );
};

export default StepTwo;
